/**
 * Rekenhulpjes voor Planning → Nog in te plannen. De echte rekenregels
 * (offerte-uren, signalen, ruimte) zitten in de engine; dit is alleen wat het
 * scherm zelf nog filtert en opmaakt — puur, dus los te testen.
 */
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

export const datumKort = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getDate()} ${MAANDEN[d.getMonth()]}`;
};

export const uur = (n) => (Number.isFinite(n) ? `${Math.round(n * 10) / 10}`.replace('.', ',') : '–');

/** Welke disciplines horen bij het tabblad? Editors zien montage + feedback, animators animatie. */
export const disciplinesVoorTab = (tab) => {
  if (tab === 'editors') return ['montage', 'feedback'];
  if (tab === 'animators') return ['animatie'];
  return ['montage', 'animatie', 'feedback'];
};

/**
 * Hoort dit project in blok A voor de gekozen disciplines? Zonder offerte-uren
 * weten we niet hoeveel er moet; dan tonen we het project bij "Alles" altijd
 * en bij een tabblad alleen als er taken zonder eigenaar in die discipline zijn.
 */
export const heeftOpenWerk = (rij, disciplines) => {
  if (rij.staat === 'geen_offerte_uren') {
    return disciplines.length === 3
      || disciplines.some((d) => (rij.disciplines?.[d]?.aantalZonderEigenaar || 0) > 0);
  }
  return disciplines.some((d) => (rij.disciplines?.[d]?.open || 0) > 0);
};

// ── weektijdlijn per project ────────────────────────────────────────────────
// Het Planner-To-Do-raster van vier dagen-weken paste niet: taken van maanden
// terug stapelden op de eerste kolom en taken in oktober werden als "geen datum"
// getekend (Joris, 9 sep). De tijdlijn vouwt zich nu om het project heen, per
// week, van de eerste taak tot de laatste deadline.

const DAG_MS = 24 * 60 * 60 * 1000;
export const MAX_WEKEN = 26;

export const maandagVan = (d) => {
  const x = new Date(d); x.setHours(0, 0, 0, 0);
  const dag = (x.getDay() + 6) % 7; // ma = 0
  x.setDate(x.getDate() - dag);
  return x;
};
const isoVan = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const naarDatum = (v) => { if (!v) return null; const d = new Date(`${String(v).slice(0, 10)}T00:00:00`); return Number.isNaN(d.getTime()) ? null : d; };
const weekNummer = (d) => {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dag = x.getUTCDay() || 7; x.setUTCDate(x.getUTCDate() + 4 - dag);
  const jaarStart = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil((((x - jaarStart) / DAG_MS) + 1) / 7);
};

/**
 * De weken die de tijdlijn toont: van de maandag van (vandaag of de vroegste
 * taak, wat eerder is, maar niet meer dan 8 weken terug) tot de vrijdag van de
 * laatste taak/deadline (minstens 4 weken vooruit), gemaximeerd op MAX_WEKEN.
 */
export function weekVenster({ taken = [], deadlines = [], vandaag = new Date() }) {
  const datums = [];
  taken.forEach((t) => { [t.startDate, t.endDate, t.deadline].forEach((v) => { const d = naarDatum(v); if (d) datums.push(d); }); });
  deadlines.forEach((v) => { const d = naarDatum(v); if (d) datums.push(d); });
  const nu = maandagVan(vandaag);
  const vroegste = datums.length ? new Date(Math.min(...datums)) : nu;
  const laatste = datums.length ? new Date(Math.max(...datums)) : nu;
  let start = maandagVan(new Date(Math.min(nu, Math.max(vroegste, nu - 8 * 7 * DAG_MS))));
  let eind = maandagVan(new Date(Math.max(laatste, nu.getTime() + 4 * 7 * DAG_MS)));
  let aantal = Math.round((eind - start) / (7 * DAG_MS)) + 1;
  if (aantal > MAX_WEKEN) { aantal = MAX_WEKEN; eind = new Date(start); eind.setDate(start.getDate() + (MAX_WEKEN - 1) * 7); }
  const weken = [];
  for (let i = 0; i < aantal; i += 1) {
    // Via setDate, niet via milliseconden: over de zomertijdgrens heen schoof
    // de kolom anders een uur — en dus een dag — op.
    const ma = new Date(start); ma.setDate(start.getDate() + i * 7);
    weken.push({ iso: isoVan(ma), maandag: ma, nummer: weekNummer(ma), maand: ma.getMonth(),
      label: `${ma.getDate()} ${['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'][ma.getMonth()]}`,
      huidig: ma.getTime() === nu.getTime(), verleden: ma < nu });
  }
  const vrijdag = new Date(eind); vrijdag.setDate(eind.getDate() + 6);
  return { weken, start, eind: vrijdag, vandaag: nu, afgekapt: datums.length ? new Date(Math.max(...datums)) > vrijdag : false };
}

/** Kolom (weekindex) en breedte in weken voor een taak; null als hij geen datum heeft. */
export function plaatsInVenster(taak, venster) {
  const s = naarDatum(taak.startDate) || naarDatum(taak.endDate);
  if (!s) return null;
  const e = naarDatum(taak.endDate) || s;
  const wStart = Math.round((maandagVan(s) - venster.start) / (7 * DAG_MS));
  const wEind = Math.round((maandagVan(e) - venster.start) / (7 * DAG_MS));
  const n = venster.weken.length;
  if (wEind < 0) return { buiten: 'eerder' };
  if (wStart >= n) return { buiten: 'later' };
  const kolom = Math.max(0, wStart);
  const breedte = Math.max(1, Math.min(n - 1, wEind) - kolom + 1);
  return { kolom, breedte };
}

/** Balken in rijen (lanes) leggen zodat ze elkaar niet overlappen. */
export function legInLanes(items) {
  const lanes = [];
  return [...items].sort((a, b) => a.kolom - b.kolom || b.breedte - a.breedte).map((it) => {
    let lane = lanes.findIndex((vrij) => vrij <= it.kolom);
    if (lane === -1) { lane = lanes.length; lanes.push(0); }
    lanes[lane] = it.kolom + it.breedte;
    return { ...it, lane };
  });
}

/** De samenvatting boven de tijdlijn: wat betekent "deels gepland" hier? */
export function samenvatDiscipline(d = {}) {
  const gepland = Number(d.gepland) || 0;
  const verstreken = Number(d.verstreken) || 0;
  return {
    offerte: Number(d.offerte) || 0,
    toekomst: Math.max(0, gepland - verstreken),
    verstreken,
    zonderDatum: Number(d.toegewezen) || 0,
    zonderEigenaar: Number(d.zonderEigenaar) || 0,
    open: Number(d.open) || 0,
    geteld: Number(d.geteld) || 0,
  };
}
